import React, { useEffect, useState } from 'react'
import { Modal, ProgressBar, Spinner, Button, Alert } from 'react-bootstrap'
import { AiParsingProgressProps } from '../types'

export const AiParsingProgress: React.FC<AiParsingProgressProps> = ({
  isVisible,
  fileName,
  onComplete,
  onError,
  onCancel
}) => {
  const [progress, setProgress] = useState(0)
  const [currentStep, setCurrentStep] = useState('Initializing...')
  const [estimatedTime, setEstimatedTime] = useState<number | undefined>()

  // Simulate processing steps for better UX
  useEffect(() => {
    if (!isVisible) {
      setProgress(0)
      setCurrentStep('Initializing...')
      return
    }

    const steps = [
      { progress: 10, step: 'Validating PDF file...', duration: 500 },
      { progress: 50, step: 'Extracting business information...', duration: 2000 },
      { progress: 95, step: 'Finalizing data extraction...', duration: 1000 }
    ]

    let currentStepIndex = 0
    let timeoutId: number

    const processNextStep = () => {
      if (currentStepIndex < steps.length) {
        const step = steps[currentStepIndex]
        setProgress(step.progress)
        setCurrentStep(step.step)
        
        timeoutId = setTimeout(() => {
          currentStepIndex++
          processNextStep()
        }, step.duration)
      }
    }

    processNextStep()

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [isVisible])

  const formatTime = (ms: number): string => {
    const seconds = Math.ceil(ms / 1000)
    if (seconds < 60) {
      return `${seconds} second${seconds !== 1 ? 's' : ''}`
    }
    const minutes = Math.ceil(seconds / 60)
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    }
  }

  return (
    <Modal
      show={isVisible}
      backdrop="static"
      keyboard={false}
      centered
      size="lg"
    >
      <Modal.Header className="border-0 pb-0">
        <Modal.Title className="w-100 text-center">
          <div className="d-flex align-items-center justify-content-center">
            <i className="bi bi-robot me-2 text-primary" style={{ fontSize: '1.5rem' }}></i>
            Autofilling Your Application Form
          </div>
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body className="px-4 pb-4">
        <div className="text-center mb-4">
          <div className="mb-3">
            <Spinner 
              animation="border" 
              variant="primary" 
              className="me-2"
              style={{ width: '2rem', height: '2rem' }}
            />
          </div>
          
          {fileName && (
            <p className="text-muted mb-3">
              <i className="bi bi-file-earmark-pdf me-1"></i>
              Processing: <strong>{fileName}</strong>
              {fileName.includes('application files') && (
                <span className="d-block small mt-1">
                  ⚡ Enhanced with multiple owner detection
                </span>
              )}
            </p>
          )}
          
          <div className="mb-3">
            <ProgressBar 
              now={progress} 
              variant="primary"
              style={{ height: '12px' }}
              className="mb-2"
            />
            <div className="d-flex justify-content-between small text-muted">
              <span>{progress}% complete</span>
              {estimatedTime && (
                <span>~{formatTime(estimatedTime)} remaining</span>
              )}
            </div>
          </div>
          
          <p className="text-primary fw-medium mb-3">
            <i className="bi bi-gear-fill me-2 spin"></i>
            {currentStep}
          </p>
          
          <div 
            className="text-start small mb-3"
            style={{
              backgroundColor: '#f1f3f4',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '12px 16px'
            }}
          >
            <div className="d-flex align-items-start">
              <i className="bi bi-info-circle me-2 mt-1" style={{ color: '#02225b' }}></i>
              <div>
                <strong style={{ color: '#02225b' }}>What's happening:</strong>
                <ul className="mb-0 mt-2 ps-3" style={{ color: '#374151' }}>
                  <li>Uploading your pdf</li>
                  <li>Parsing information</li>
                  <li>Organizing data</li>
                  <li>Preparing to auto-fill your application form</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="text-muted small">
            <i className="bi bi-shield-check me-1"></i>
            Your data is processed securely and not stored permanently
          </div>
        </div>
      </Modal.Body>
      
      {onCancel && (
        <Modal.Footer className="border-0 pt-0">
          <Button variant="outline-secondary" onClick={handleCancel}>
            Cancel Processing
          </Button>
        </Modal.Footer>
      )}
      
      <style>{`
        .spin {
          animation: spin 2s linear infinite;
        }
        
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </Modal>
  )
}
